import React from 'react';
import { GetStaticProps, GetStaticPaths } from 'next';
import shuffle from 'lodash/shuffle';

import { InstitutionTemplate, InstitutionPageProps } from 'src/templates';
import { toKebabCase, replace } from 'src/lib/utils';
import data from 'src/store/institutions/institution_data.json';
import {
  pageHeaderData,
  footerExtensionData,
  supportingTextData,
  featuresPlaidIntegrationData,
  introSectionData,
} from 'src/store/institutions/institutions-static-data';
import { interactiveData } from 'src/store/interactive-data';
import { staticData } from 'src/store/static-data';
import { isTesting } from 'src/lib/constants';
const InstitutionPage = (props: InstitutionPageProps): JSX.Element => {
  return <InstitutionTemplate {...props} />;
};

const mapToAccountTypes = (accountMapping) => {
  return accountMapping !== null
    ? Object.keys(accountMapping).map((acct) => {
        return acct.charAt(0).toUpperCase() + acct.slice(1);
      })
    : [];
};

const getRandomInstitutions = ({ institution_data: institutionData }) => {
  return shuffle(institutionData)
    .slice(0, 3)
    .map((item) => {
      return {
        title: item.name,
        href: `/institutions/${toKebabCase(item.name)}/`,
      };
    });
};

export const getStaticPaths: GetStaticPaths = async () => {
  const paths = data['institution_data'].map((inst) => {
    return {
      params: { filp: toKebabCase(inst.name) },
    };
  });

  return {
    paths: isTesting ? [] : paths,
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps<InstitutionPageProps> = async ({
  params = {},
}) => {
  const entry = data['institution_data'].find((inst) => {
    return params.filp === toKebabCase(inst.name);
  });

  return {
    props: {
      institutionName: `${entry.name}`,
      metaDescription: `Plaid helps you connect with financial services. Learn more about how Plaid can help developers access ${entry.name} data.`,
      metaTitle: `${entry.name} API & Data Solutions`,
      pageSlug: params.filp,
      pageHeaderProps: {
        ...pageHeaderData,
        heading: {
          text: `Integrate ${entry.name} Data`,
        },
        subheading: {
          text: `Plaid delivers a reliable and easy integration to ${entry.name} so that you can seamlessly power your Fintech innovation. Learn more about our data connectivity solutions.`,
        },
        image: {
          ...pageHeaderData.image,
          alt: `Integrate ${entry.name} Data`,
        },
      },
      footerExtensionProps: footerExtensionData,
      supportingTextProps: supportingTextData,
      featuresPlaidIntegrationProps: {
        ...featuresPlaidIntegrationData,
        items: featuresPlaidIntegrationData.items.map((item) => {
          return {
            ...item,
            content: replace({
              toModify: item.content,
              pattern: '{{FI_NAME}}',
              replacement: entry.name,
            }),
          };
        }),
      },
      featuresInstitutionMetadataProps: {
        hasThreeColumns: entry.account_mapping !== null,
        accountTypes: mapToAccountTypes(entry.account_mapping),
        products: entry.products,
        country: entry.country,
      },

      introSectionProps: {
        ...introSectionData,
        items: introSectionData.items.map((item) => {
          return {
            ...item,
            description: replace({
              toModify: item.description,
              pattern: /({{FI_NAME}})|({{PRODUCTS}})/,
              replacement: (m, p1, p2) => {
                if (p1) return entry.name;
                if (p2) {
                  if (entry?.products?.length >= 3) {
                    return (
                      entry.products.slice(0, 2).join(', ') +
                      ' and ' +
                      entry.products[2]
                    );
                  } else if (entry?.products?.length >= 2) {
                    return entry.products[0] + ' and ' + entry.products[1];
                  } else {
                    return (
                      entry?.products?.join(', ') || 'Assets, Auth, and Balance'
                    );
                  }
                }
              },
            }),
          };
        }),
      },
      allEndpointDemoData: { ...interactiveData, ...staticData },
      internalLinksProps: getRandomInstitutions(data),
    },
  };
};

export default InstitutionPage;
